import { useMemo, useState, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import type {
  QlikReloadRow,
  ReloadHistoryView,
  ReloadStatus,
} from "@qlik-scheduler/shared";

import { ApiError, apiRequest } from "../../api/client.js";
import {
  useConnections,
  useQlikReloads,
  useReloadHistory,
} from "../../api/queries.js";
import { Button } from "../../components/Button.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { Skeleton } from "../../components/Skeleton.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Table, TBody, Td, Th, THead, Tr } from "../../components/Table.js";

const STATUSES: ReloadStatus[] = ["QUEUED", "RELOADING", "SUCCEEDED", "FAILED", "CANCELLED"];
const PAGE_SIZE = 50;

export function HistoryTab() {
  const [appId, setAppId] = useState("");
  const [status, setStatus] = useState<ReloadStatus | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Set when the user clicks a row's "Bulk" chip to drill into a single
  // bulk-reload batch. Surfaced as a removable pill above the filters.
  const [operationId, setOperationId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [includeExternal, setIncludeExternal] = useState(false);
  const connectionsQ = useConnections();
  const activeConnections = useMemo(
    () => (connectionsQ.data ?? []).filter((c) => c.isActive),
    [connectionsQ.data],
  );
  const connectionIds = useMemo(
    () => activeConnections.map((c) => c.id),
    [activeConnections],
  );
  const qlik = useQlikReloads(connectionIds, "all", {
    enabled: includeExternal,
  });

  // Per-connection full apps fetch — drives the "App" dropdown. Uses the
  // server-side auto-paginating /api/apps/all so we get every app, not just
  // one Qlik page (the items endpoint caps at 100/page; tenants can have
  // more). 5-min server cache + 10-min react-query staleTime so opening the
  // dropdown is essentially free after the first fetch.
  const appsQueries = useQueries({
    queries: activeConnections.map((c) => ({
      queryKey: ["apps", "all", c.id] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        apiRequest<{ data: Array<{ appId: string; name: string }> }>(
          `/api/apps/all?connectionId=${c.id}`,
          { signal },
        ),
      staleTime: 10 * 60 * 1000,
    })),
  });

  // Sorted flat list of {appId, label} pairs feeding the dropdown.
  // When >1 connection is active, suffix the label with the connection name
  // so duplicates are disambiguated.
  const appOptions = useMemo(() => {
    const multi = activeConnections.length > 1;
    const out: Array<{ appId: string; label: string }> = [];
    appsQueries.forEach((q, i) => {
      const conn = activeConnections[i];
      if (!q.data || !conn) return;
      for (const app of q.data.data) {
        out.push({
          appId: app.appId,
          label: multi ? `${app.name} · ${conn.connectionName}` : app.name,
        });
      }
    });
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [appsQueries, activeConnections]);

  const args = {
    ...(appId ? { appId } : {}),
    ...(status ? { status } : {}),
    ...(operationId ? { operationId } : {}),
    ...(from ? { from: new Date(from).toISOString() } : {}),
    ...(to ? { to: new Date(to).toISOString() } : {}),
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, error } = useReloadHistory(args, {
    placeholderData: (prev) => prev,
  });

  function resetPage() {
    setOffset(0);
  }

  function toggleExpand(reloadId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(reloadId)) next.delete(reloadId);
      else next.add(reloadId);
      return next;
    });
  }

  const localRows: ReloadHistoryView[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // When "Include external" is on, splice in any Qlik rows that aren't
  // already represented by a local row (matched on qlikReloadId). External-
  // only rows lack our internal fields (operationId, errorMessage, etc) —
  // we render them with what Qlik gives us. The combined list is sorted
  // newest-first.
  const rows = useMemo(() => {
    if (!includeExternal) {
      return localRows.map((r) => ({ kind: "local" as const, row: r }));
    }
    const localIds = new Set(localRows.map((r) => r.qlikReloadId).filter(Boolean));
    // External rows: drop any already represented locally, AND apply the
    // same filters the server applied to the local rows (appId / status /
    // date range) so toggling "Include external" doesn't flood the table
    // with unrelated apps when the user has narrowed by app.
    const fromIso = from ? new Date(from).getTime() : null;
    const toIso = to ? new Date(to).getTime() : null;
    const external = (qlik.data?.data ?? []).filter((q) => {
      if (localIds.has(q.qlikReloadId)) return false;
      if (appId && q.appId !== appId) return false;
      if (status && q.status !== status) return false;
      if (fromIso != null && q.startTime) {
        if (new Date(q.startTime).getTime() < fromIso) return false;
      }
      if (toIso != null && q.startTime) {
        if (new Date(q.startTime).getTime() > toIso) return false;
      }
      return true;
    });
    const combined: Array<
      | { kind: "local"; row: ReloadHistoryView }
      | { kind: "external"; row: QlikReloadRow }
    > = [
      ...localRows.map((r) => ({ kind: "local" as const, row: r })),
      ...external.map((r) => ({ kind: "external" as const, row: r })),
    ];
    combined.sort((a, b) => {
      const ta = a.kind === "local" ? a.row.startTime : (a.row.startTime ?? "");
      const tb = b.kind === "local" ? b.row.startTime : (b.row.startTime ?? "");
      return new Date(tb).getTime() - new Date(ta).getTime();
    });
    return combined;
  }, [localRows, qlik.data, includeExternal, appId, status, from, to]);

  return (
    <div className="space-y-4">
      {operationId ? (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-blue-light bg-blue-light/30 px-4 py-3 text-[12px] text-ink">
          <span className="field-label">Filtered to bulk operation</span>
          <code className="rounded bg-white px-2 py-0.5 text-[12px] text-ink-soft">
            {operationId}
          </code>
          <button
            type="button"
            className="ml-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-blue hover:text-blue-dark"
            onClick={() => {
              setOperationId(null);
              resetPage();
            }}
          >
            Clear
          </button>
        </div>
      ) : null}
      <div className="card p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="hist-app" className="field-label mb-1.5 block">
              App
            </label>
            <select
              id="hist-app"
              value={appId}
              onChange={(e) => {
                setAppId(e.target.value);
                resetPage();
              }}
              className="input"
              disabled={appsQueries.some((q) => q.isLoading) && appOptions.length === 0}
            >
              <option value="">
                {appOptions.length === 0 &&
                appsQueries.some((q) => q.isLoading)
                  ? "Loading apps…"
                  : "Any app"}
              </option>
              {/* If the current appId isn't in the fetched list (deleted app,
                  or app outside the current connections), keep it selectable
                  so the filter doesn't silently clear when the user navigates
                  back. */}
              {appId &&
              !appOptions.some((o) => o.appId === appId) ? (
                <option value={appId}>{`Unknown app (${appId.slice(0, 8)}…)`}</option>
              ) : null}
              {appOptions.map((o) => (
                <option key={o.appId} value={o.appId}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="hist-status" className="field-label mb-1.5 block">
              Status
            </label>
            <select
              id="hist-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as ReloadStatus | "");
                resetPage();
              }}
              className="input"
            >
              <option value="">Any</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="hist-from" className="field-label mb-1.5 block">
              From
            </label>
            <input
              id="hist-from"
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                resetPage();
              }}
              className="input"
            />
          </div>
          <div>
            <label htmlFor="hist-to" className="field-label mb-1.5 block">
              To
            </label>
            <input
              id="hist-to"
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                resetPage();
              }}
              className="input"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={includeExternal}
              onChange={(e) => setIncludeExternal(e.target.checked)}
              className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
            />
            Include external reloads (Hub + Qlik native scheduler)
          </label>
          {includeExternal && qlik.isLoading ? (
            <span className="text-[12px] text-ink-soft">Loading external…</span>
          ) : null}
        </div>
      </div>

      {isLoading && rows.length === 0 ? (
        <div className="card p-4 space-y-3" aria-label="Loading history">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton width="15%" height={12} />
              <Skeleton width="25%" height={12} />
              <Skeleton width="15%" height={12} />
              <Skeleton width="15%" height={12} />
              <Skeleton width="15%" height={12} />
            </div>
          ))}
        </div>
      ) : null}
      {error ? <ErrorAlert>{describeError(error)}</ErrorAlert> : null}

      {!isLoading && rows.length === 0 && !error ? (
        <div className="card empty-surface">
          <p className="text-base font-semibold text-ink">No reload history</p>
          <p>No reloads have been recorded yet.</p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Table>
            <THead>
              <tr>
                <Th>Start Time</Th>
                <Th>App</Th>
                <Th>Source</Th>
                <Th>Status</Th>
                <Th>Duration</Th>
                <Th>Triggered By</Th>
                <Th className="text-right">Details</Th>
              </tr>
            </THead>
            <TBody>
              {rows.map((entry) => {
                if (entry.kind === "external") {
                  const q = entry.row;
                  return (
                    <Tr key={`ext:${q.qlikReloadId}`}>
                      <Td>
                        {q.startTime ? formatDateTime(q.startTime) : "—"}
                      </Td>
                      <Td>{q.appName ?? `App ${q.appId.slice(0, 8)}…`}</Td>
                      <Td>
                        <SourceBadge source={q.source} />
                      </Td>
                      <Td>
                        <StatusBadge tone={statusTone(q.status)}>
                          {q.status}
                        </StatusBadge>
                      </Td>
                      <Td>{formatExternalDuration(q)}</Td>
                      <Td>
                        {q.source === "hub"
                          ? "Hub user"
                          : q.source === "qlik-native"
                            ? "Qlik scheduler"
                            : "—"}
                      </Td>
                      <Td className="text-right text-[12px] text-ink-soft">
                        external
                      </Td>
                    </Tr>
                  );
                }
                const r = entry.row;
                const isOpen = expanded.has(r.reloadId);
                return (
                  <FragmentRow key={r.reloadId}>
                    <Tr>
                      <Td>{formatDateTime(r.startTime)}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <span>{r.appName}</span>
                          {r.operationId ? (
                            <button
                              type="button"
                              className="chip bg-blue-light text-blue-dark transition hover:bg-blue hover:text-white"
                              title={`Filter history to bulk operation ${r.operationId}`}
                              onClick={() => {
                                setOperationId(r.operationId);
                                resetPage();
                              }}
                            >
                              Bulk
                            </button>
                          ) : null}
                        </div>
                      </Td>
                      <Td>
                        <SourceBadge source="qlik-scheduler" />
                      </Td>
                      <Td>
                        <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge>
                      </Td>
                      <Td>{r.durationSeconds != null ? formatDuration(r.durationSeconds) : "—"}</Td>
                      <Td>
                        {r.triggeredByName ?? (r.reloadType === "SCHEDULED" ? "Scheduler" : "—")}
                      </Td>
                      <Td className="text-right">
                        <Button variant="ghost-sm" onClick={() => toggleExpand(r.reloadId)}>
                          {isOpen ? "Hide" : "Show"}
                        </Button>
                      </Td>
                    </Tr>
                    {isOpen ? (
                      <tr className="border-b border-line bg-bg transition">
                        <Td colSpan={7} className="text-xs text-ink-soft">
                          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                            <DetailRow label="Type" value={r.reloadType} />
                            <DetailRow label="Reload ID" value={r.reloadId} />
                            <DetailRow label="Qlik reload ID" value={r.qlikReloadId ?? "—"} />
                            <DetailRow
                              label="End time"
                              value={r.endTime ? formatDateTime(r.endTime) : "—"}
                            />
                            <DetailRow label="Retry attempt" value={String(r.retryAttempt)} />
                            <DetailRow
                              label="Partial reload"
                              value={r.partialReload ? "yes" : "no"}
                            />
                            <DetailRow label="Error code" value={r.errorCode ?? "—"} />
                            {r.operationId ? (
                              <DetailRow
                                label="Bulk operation"
                                value={r.operationId}
                              />
                            ) : null}
                          </dl>
                          {r.errorMessage ? (
                            <p className="mt-3 whitespace-pre-wrap rounded-card border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                              {r.errorMessage}
                            </p>
                          ) : null}
                          {r.qlikLogUrl ? (
                            <p className="mt-3">
                              <a
                                href={r.qlikLogUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-sm font-semibold uppercase tracking-[0.06em] text-blue hover:text-blue-dark"
                              >
                                View Qlik Log →
                              </a>
                            </p>
                          ) : null}
                        </Td>
                      </tr>
                    ) : null}
                  </FragmentRow>
                );
              })}
            </TBody>
          </Table>

          <div className="flex items-center justify-between text-[12px] text-ink-soft">
            <span>
              Page {page} of {totalPages} · {total} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost-sm"
                disabled={offset === 0 || isLoading}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="ghost-sm"
                disabled={offset + PAGE_SIZE >= total || isLoading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// React doesn't let us return adjacent <tr>s inline from a .map and keep React
// keys clean; use a Fragment wrapper component.
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="font-semibold text-ink-soft">{label}:</dt>
      <dd className="break-all text-ink">{value}</dd>
    </div>
  );
}

// Format a duration in seconds as a compact mixed unit string.
//   45      → "45s"
//   60      → "1m"
//   135     → "2m 15s"
//   3725    → "1h 2m 5s"
function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(" ") || "0s";
}

// Qlik's /api/v1/reloads omits `duration` on many rows. Compute it from
// startTime + endTime when we have both; honour the upstream value when
// it's an ISO 8601 duration ("PT1M30S") by parsing it; otherwise "—".
function formatExternalDuration(q: QlikReloadRow): string {
  if (q.startTime && q.endTime) {
    const ms = new Date(q.endTime).getTime() - new Date(q.startTime).getTime();
    if (Number.isFinite(ms) && ms >= 0) {
      return formatDuration(Math.round(ms / 1000));
    }
  }
  if (q.duration) {
    const m = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(q.duration);
    if (m) {
      const hours = m[1] ? parseInt(m[1], 10) : 0;
      const mins = m[2] ? parseInt(m[2], 10) : 0;
      const secs = m[3] ? Math.round(parseFloat(m[3])) : 0;
      return formatDuration(hours * 3600 + mins * 60 + secs);
    }
    return q.duration;
  }
  return "—";
}

function SourceBadge({ source }: { source: QlikReloadRow["source"] }) {
  switch (source) {
    case "qlik-scheduler":
      return <StatusBadge tone="blue">qlik-scheduler</StatusBadge>;
    case "hub":
      return <StatusBadge tone="yellow">Hub</StatusBadge>;
    case "qlik-native":
      return <StatusBadge tone="grey">Qlik scheduler</StatusBadge>;
    case "external":
      return <StatusBadge tone="grey">External</StatusBadge>;
  }
}

function statusTone(s: ReloadStatus) {
  switch (s) {
    case "SUCCEEDED":
      return "green";
    case "FAILED":
      return "red";
    case "CANCELLED":
      return "grey";
    case "QUEUED":
      return "blue";
    case "RELOADING":
      return "blue";
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
